/**
 * Shared formatting helpers for the reports2 module.
 *
 * Conventions (from the Lotus Reporting spec §7):
 *	- Dates are always DD-Mmm-YY (e.g. 08-Jul-26). No purely-numeric dates anywhere.
 *	- Time is minutes, formatted as "Xh Ym".
 *	- Currency is the customer's local currency only, never converted.
 */


// Dates: the DD-Mmm-YY rule is portal-wide, so it lives in /helpers/date-formatting.js
// rather than being re-implemented here. `formatReportDate` and `toIsoDate` below keep
// their names and behaviour and simply delegate.
import { formatDisplayDate, toIsoDate as toIsoDateShared } from "/helpers/date-formatting.js";

// Likewise the "Xh Ym" rule: portal-wide, so it lives in /helpers/duration-formatting.js and is
// re-exported here under its existing name ("3h 25m" / "45m" / "0m").
import { formatMinutes } from "/helpers/duration-formatting.js";

export { formatMinutes };


/**
 * Format a whole number with thousands separators (12345 -> "12,345").
 */
export function formatNumber(value)
{
	return Math.round(value || 0).toLocaleString();
}


/**
 * Format a money amount compactly: $1.25m / $48k / $960.
 */
export function formatMoney(amount)
{
	amount = Math.round(amount || 0);
	if (amount >= 1000000) return "$" + (Math.round(amount / 10000) / 100).toFixed(2).replace(/\.?0+$/, "") + "m";
	if (amount >= 10000) return "$" + Math.round(amount / 1000).toLocaleString() + "k";
	return "$" + amount.toLocaleString();
}


/**
 * Format a date (Date | ms | ISO string) as DD-Mmm-YY (spec §7 — never purely numeric).
 * A .NET default/unset DateTime ("0001-01-01...", year 1) renders as "—" rather
 * than the broken-looking "01-Jan-" you'd otherwise get (year 1 has no last-two-digits).
 */
export function formatReportDate(value)
{
	const local = parseIsoDateLocal(value);
	return formatDisplayDate(local || value);
}


/**
 * A bare ISO date ("2026-07-08", as the reports API returns report days) as a Date at LOCAL midnight, or null if the
 * value isn't one. `new Date("2026-07-08")` is UTC midnight, which is the previous day for anyone west of UTC, so a
 * bare date must never go through it.
 */
export function parseIsoDateLocal(value)
{
	if (typeof value !== "string") return null;

	const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
	if (match == null) return null;

	return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}


/**
 * Format a whole percentage from a ratio (0.42 -> "42%"). Returns em-dash when the base is 0.
 */
export function formatPercent(part, whole)
{
	if (!whole) return "—";
	return Math.round((part / whole) * 100) + "%";
}


/**
 * Format an already-computed ratio as a whole percentage (0.42 -> "42%").
 * Use where the API returns the ratio directly (e.g. PercentConsumed).
 */
export function formatRatioPercent(ratio)
{
	return Math.round((ratio || 0) * 100) + "%";
}


/**
 * A ratio as a CSS width, clamped to 0–100% so an over-budget bar can't overflow
 * its track. Use for bar fills and position markers.
 */
export function ratioToClampedCssWidth(ratio)
{
	const pct = (ratio || 0) * 100;
	return Math.min(Math.max(pct, 0), 100) + "%";
}


/**
 * A part/whole share as a CSS width (unclamped — the parts of a split bar are
 * measured against a shared max, so their total is 0–100% by construction).
 */
export function shareToCssWidth(part, whole)
{
	if (!whole) return "0%";
	return ((part / whole) * 100) + "%";
}


/**
 * Pluralise a noun against a count: (1, "day") -> "1 day", (3, "day") -> "3 days".
 * `plural` covers irregular forms; otherwise an "s" is appended.
 */
export function formatCount(count, singular, plural = null)
{
	const n = count || 0;
	if (n === 1) return n + " " + singular;
	return n + " " + (plural || singular + "s");
}


/**
 * Pick the aggregate figures for a value basis ("Rounded" | "Actual") off a Time-engine report node (a Group, Child or
 * Totals object from generateTimeReportAsync). Returns:
 *	{ Billable, NonBillable, Approved, Unapproved, Total,
 *	  BillableApproved, BillableUnapproved, NonBillableApproved, NonBillableUnapproved,
 *	  Duplicate }
 * Billable / NonBillable / Total include unapproved time only when the report was run with it included (Decision E); the
 * *Approved / *Unapproved fields are always the true split, so no figure ever merges unapproved time silently.
 * Duplicate is time that also sits under another team (a person in several teams that day).
 */
export function basisValues(node, valueBasis)
{
	const prefix = valueBasis === "Actual" ? "Actual" : "Rounded";

	return {
		Billable: node[prefix + "BillableMinutes"] || 0,
		NonBillable: node[prefix + "NonBillableMinutes"] || 0,
		Approved: node[prefix + "ApprovedMinutes"] || 0,
		Unapproved: node[prefix + "UnapprovedMinutes"] || 0,
		Total: node[prefix + "TotalMinutes"] || 0,
		BillableApproved: node[prefix + "BillableApprovedMinutes"] || 0,
		BillableUnapproved: node[prefix + "BillableUnapprovedMinutes"] || 0,
		NonBillableApproved: node[prefix + "NonBillableApprovedMinutes"] || 0,
		NonBillableUnapproved: node[prefix + "NonBillableUnapprovedMinutes"] || 0,
		Duplicate: node["Duplicate" + prefix + "Minutes"] || 0,
	};
}


/**
 * The "x appr · +y pend" line shown under a figure when unapproved time is included: the approved and pending parts of
 * the figure beside it, so the whole and its provisional part are always visible together (Decision E).
 * Returns null when there is nothing pending to show.
 */
export function approvedPendingLine(approvedMinutes, unapprovedMinutes, formatValue)
{
	if (!unapprovedMinutes) return null;

	return formatValue(approvedMinutes) + " appr · +" + formatValue(unapprovedMinutes) + " pend";
}

/**
 * ISO date (yyyy-mm-dd) for API requests.
 */
export function toIsoDate(value)
{
	return toIsoDateShared(value);
}
